import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { forgotPasswordApi } from '@/lib/auth/authApi';
import { ArrowLeft, Mail } from 'lucide-react';

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const fromSource = searchParams.get('fromSource');

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loginUrl = fromSource === 'site' ? `${ROUTES.LOGIN}?fromSource=site` : ROUTES.LOGIN;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await forgotPasswordApi(email);
      setIsSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'envoi");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 to-emerald-500 p-6 font-['Plus_Jakarta_Sans',sans-serif]">
      <div className='w-full max-w-md'>
        {/* Logo */}
        <div className='text-center mb-8'>
          <div className='inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4'>
            <div className='w-8 h-8 rounded-full border-[6px] border-emerald-600 border-t-transparent animate-spin-slow' />
          </div>
          <h1 className='text-3xl font-extrabold text-white mb-2'>BoulesConnect</h1>
          <p className='text-emerald-100'>Réinitialisation de votre mot de passe</p>
        </div>

        {/* Card */}
        <div className='bg-white rounded-3xl shadow-2xl p-8'>
          {!isSent ? (
            <>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Mot de passe oublié</h2>
              <p className='text-slate-500 text-sm mb-6'>
                Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </p>

              {error && (
                <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>
                  {error}
                </div>
              )}

              <form className='space-y-5' onSubmit={handleSubmit}>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-2'>Adresse email</label>
                  <div className='relative'>
                    <Mail className='w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2' />
                    <input
                      type='email'
                      placeholder='votremail@exemple.fr'
                      className='w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition'
                      required
                      maxLength={255}
                      value={email}
                      onChange={event => setEmail(event.target.value)}
                    />
                  </div>
                </div>

                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition shadow-lg shadow-emerald-200'
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le lien'}
                </button>
              </form>
            </>
          ) : (
            <div className='text-center py-4'>
              <div className='w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Mail className='w-8 h-8 text-emerald-600' />
              </div>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Email envoyé !</h2>
              <p className='text-slate-500 text-sm mb-6'>
                Si un compte est associé à <span className='font-semibold text-slate-700'>{email}</span>, vous recevrez
                un lien de réinitialisation dans quelques instants.
              </p>
              <p className='text-xs text-slate-400'>Pensez à vérifier vos spams si vous ne recevez rien.</p>
            </div>
          )}

          {/* Retour login */}
          <div className='mt-6 text-center'>
            <Link
              to={loginUrl}
              className='text-sm font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1'
            >
              <ArrowLeft className='w-4 h-4' />
              Retour à la connexion
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className='mt-8 text-center'>
          <div className='flex items-center justify-center gap-6 text-sm text-emerald-100'>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/support`}
              className='hover:text-white transition'
            >
              Aide
            </a>
            <span>•</span>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/politique-rgpd`}
              className='hover:text-white transition'
            >
              Confidentialité
            </a>
            <span>•</span>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/cgu-cgv`}
              className='hover:text-white transition'
            >
              Conditions
            </a>
          </div>
          <p className='text-emerald-200 text-sm mt-4'>&copy; 2026 BoulesConnect. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
}
